import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, X } from 'lucide-react';
import { inputClass, TIME_SLOTS } from './caseConstants';

// One modal covering every resolution action. They share the same shape — a few
// fields, one confirm — so a single component keeps the whole action bar
// consistent rather than five near-identical dialogs.

const TITLES = {
  refund: 'ISSUE REFUND',
  goodwill: 'APPLY GOODWILL CREDIT',
  reschedule: 'RESCHEDULE BOOKING',
  reassign_bay: 'REASSIGN BAY',
  reschedule_apologise: 'RESCHEDULE & APOLOGISE',
  resolve: 'CLOSE / RESOLVE CASE',
};

export default function CaseActionModal({ action, caseData, orders = [], rigs = [], linkedBooking, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // refund
  const [orderId, setOrderId] = useState(caseData.linked_order_id || orders[0]?.id || '');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  // goodwill
  const [goodwillType, setGoodwillType] = useState('points');
  const [points, setPoints] = useState('');
  const [discountValue, setDiscountValue] = useState('');

  // booking
  const [bookingId, setBookingId] = useState(caseData.linked_booking_id || '');
  const [date, setDate] = useState(linkedBooking?.date || '');
  const [timeSlot, setTimeSlot] = useState(linkedBooking?.time_slot || '');
  const [rigKey, setRigKey] = useState(linkedBooking?.rig_type || caseData.rig_id || '');

  // apology macro
  const [apologySubject, setApologySubject] = useState('Your rescheduled session — our apologies');
  const [apologyBody, setApologyBody] = useState(
    `Hi ${(caseData.customer_name || '').trim().split(/\s+/)[0] || 'there'},\n\nWe're sorry — we've had to move your session. Your new slot is confirmed below, and we've added a goodwill credit to your account as an apology.\n\nThank you for your patience.`
  );
  const [resolutionNotes, setResolutionNotes] = useState('');

  // Feedback survey — sent on close unless the admin turns it off. Defaults to
  // whichever channel opened the case.
  const [sendSurvey, setSendSurvey] = useState(true);
  const [feedbackChannel, setFeedbackChannel] = useState(
    caseData.channel === 'sms' ? 'sms' : 'email'
  );

  const submit = async () => {
    setError('');
    setSaving(true);
    try {
      let res;

      if (action === 'resolve') {
        if (!resolutionNotes.trim()) throw new Error('Add a note describing how this was resolved.');
        res = await base44.functions.invoke('caseUpdate', {
          caseId: caseData.id,
          status: 'resolved',
          resolution_notes: resolutionNotes,
        });
        if (res.data?.error) throw new Error(res.data.error);

        // The case is closed either way — a survey that fails is reported so it
        // can be re-sent, rather than leaving the case open.
        if (sendSurvey) {
          const survey = await base44.functions.invoke('sendCaseFeedbackSurvey', {
            caseId: caseData.id,
            channel: feedbackChannel,
          });
          if (survey.data?.error) {
            throw new Error(`Case closed, but the survey failed: ${survey.data.error}`);
          }
        }
      } else if (action === 'refund') {
        if (!orderId) throw new Error('Choose the order to refund.');
        if (!amount || Number(amount) <= 0) throw new Error('Enter a refund amount.');
        if (!reason.trim()) throw new Error('A reason is required.');
        res = await base44.functions.invoke('caseResolution', {
          caseId: caseData.id,
          action: 'refund',
          orderId,
          amount: Number(amount),
          reason,
        });
      } else if (action === 'goodwill') {
        if (!reason.trim()) throw new Error('A reason is required.');
        if (goodwillType === 'points' && !Number(points)) throw new Error('Enter a points amount.');
        if (goodwillType === 'discount_code' && !Number(discountValue)) {
          throw new Error('Enter a discount value.');
        }
        res = await base44.functions.invoke('caseResolution', {
          caseId: caseData.id,
          action: 'goodwill',
          action_type: goodwillType,
          points: goodwillType === 'points' ? Number(points) : undefined,
          discount_value: goodwillType === 'discount_code' ? Number(discountValue) : undefined,
          reason,
        });
      } else {
        if (!bookingId) throw new Error('No booking is linked to this case.');
        const payload = {
          caseId: caseData.id,
          action,
          bookingId,
          date: date || undefined,
          time_slot: timeSlot || undefined,
          rig_key: action === 'reassign_bay' ? rigKey : undefined,
          reason,
        };
        if (action === 'reschedule_apologise') {
          payload.points = Number(points) || 0;
          payload.apology_subject = apologySubject;
          payload.apology_body = apologyBody;
          payload.resolution_notes = resolutionNotes;
        }
        res = await base44.functions.invoke('caseResolution', payload);
      }

      if (res.data?.error) throw new Error(res.data.error);
      if (onDone) onDone();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Action failed.');
    }
    setSaving(false);
  };

  const Field = ({ label, children }) => (
    <div>
      <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">{label}</p>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-lg border border-white/10 bg-[#0d0d0f] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 sticky top-0 bg-[#0d0d0f]">
          <p className="text-white text-xs font-bold tracking-widest">{TITLES[action] || 'ACTION'}</p>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {action === 'refund' && (
            <>
              <Field label="ORDER">
                <select value={orderId} onChange={(e) => setOrderId(e.target.value)} className={inputClass}>
                  <option value="">Select an order…</option>
                  {orders.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.invoice_number || o.product_name || 'Order'} — £{Number(o.amount || 0).toFixed(2)} · {o.status}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="REFUND AMOUNT (£)">
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={inputClass}
                  placeholder="0.00"
                />
              </Field>
              <p className="text-white/25 text-[10px]">
                The refund is processed against the original payment and a branded credit note is
                generated and emailed to the customer as part of it.
              </p>
            </>
          )}

          {action === 'goodwill' && (
            <>
              <Field label="CREDIT TYPE">
                <select
                  value={goodwillType}
                  onChange={(e) => setGoodwillType(e.target.value)}
                  className={inputClass}
                >
                  <option value="points">Loyalty points</option>
                  <option value="discount_code">One-time discount code</option>
                </select>
              </Field>
              {goodwillType === 'points' ? (
                <Field label="POINTS">
                  <input
                    type="number"
                    value={points}
                    onChange={(e) => setPoints(e.target.value)}
                    className={inputClass}
                    placeholder="e.g. 250"
                  />
                </Field>
              ) : (
                <Field label="DISCOUNT VALUE (£)">
                  <input
                    type="number"
                    step="0.01"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className={inputClass}
                    placeholder="0.00"
                  />
                </Field>
              )}
            </>
          )}

          {(action === 'reschedule' || action === 'reassign_bay' || action === 'reschedule_apologise') && (
            <>
              {!caseData.linked_booking_id && (
                <Field label="BOOKING ID">
                  <input
                    value={bookingId}
                    onChange={(e) => setBookingId(e.target.value)}
                    className={inputClass}
                    placeholder="Paste a booking id, or attach one on the case"
                  />
                </Field>
              )}
              {action !== 'reassign_bay' && (
                <>
                  <Field label="NEW DATE">
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="NEW TIME SLOT">
                    <select value={timeSlot} onChange={(e) => setTimeSlot(e.target.value)} className={inputClass}>
                      <option value="">Keep current</option>
                      {TIME_SLOTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </Field>
                </>
              )}
              {action === 'reassign_bay' && (
                <Field label="ALTERNATE BAY">
                  <select value={rigKey} onChange={(e) => setRigKey(e.target.value)} className={inputClass}>
                    <option value="">Select a bay…</option>
                    {rigs
                      .filter((r) => !r.maintenance)
                      .map((r) => (
                        <option key={r.key} value={r.key}>{r.name}</option>
                      ))}
                  </select>
                </Field>
              )}
            </>
          )}

          {action === 'reschedule_apologise' && (
            <>
              <Field label="GOODWILL POINTS">
                <input
                  type="number"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 250 (leave blank for none)"
                />
              </Field>
              <Field label="APOLOGY SUBJECT">
                <input
                  value={apologySubject}
                  onChange={(e) => setApologySubject(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="APOLOGY MESSAGE">
                <textarea
                  value={apologyBody}
                  onChange={(e) => setApologyBody(e.target.value)}
                  rows={7}
                  className={`${inputClass} resize-none leading-relaxed`}
                />
              </Field>
              <p className="text-white/25 text-[10px]">
                Confirms the new slot, credits the customer, sends the apology and resolves the case.
              </p>
            </>
          )}

          {action === 'resolve' && (
            <>
              <Field label="RESOLUTION NOTES">
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  rows={5}
                  className={`${inputClass} resize-none leading-relaxed`}
                  placeholder="What was done to close this out?"
                />
              </Field>

              <div className="border-t border-white/5 pt-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendSurvey}
                    onChange={(e) => setSendSurvey(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#dd1f26] shrink-0"
                  />
                  <span className="text-white/70 text-xs leading-relaxed">
                    Send resolution feedback survey to customer
                  </span>
                </label>

                {sendSurvey && (
                  <div>
                    <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">
                      SEND VIA
                    </p>
                    <div className="flex items-center gap-5">
                      {['email', 'sms'].map((ch) => (
                        <label key={ch} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="feedback-channel"
                            value={ch}
                            checked={feedbackChannel === ch}
                            onChange={() => setFeedbackChannel(ch)}
                            className="w-4 h-4 accent-[#dd1f26]"
                          />
                          <span className="text-white/70 text-xs uppercase tracking-wider">
                            {ch === 'email' ? 'Email' : 'SMS'}
                          </span>
                        </label>
                      ))}
                    </div>

                    {feedbackChannel === 'email' && !caseData.customer_email && (
                      <p className="text-amber-400/80 text-[11px] mt-2">
                        No email address on this case — add one or send by SMS.
                      </p>
                    )}
                    {feedbackChannel === 'sms' && !caseData.customer_phone && (
                      <p className="text-amber-400/80 text-[11px] mt-2">
                        No phone number on this case — add one or send by email.
                      </p>
                    )}

                    <p className="text-white/25 text-[10px] mt-2 leading-relaxed">
                      A single polite reminder goes out 24 hours later if they don't reply.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {action !== 'resolve' && action !== 'reschedule_apologise' && (
            <Field label="REASON / NOTES">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className={`${inputClass} resize-none`}
                placeholder="Recorded on the case timeline"
              />
            </Field>
          )}

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              className="border border-white/10 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-4 py-2.5 transition-colors"
            >
              CANCEL
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-5 py-2.5 transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'WORKING…' : 'CONFIRM'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}